import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useWindowWidth from '../hooks/useWindowWidth';
import logo from '../assets/logo.svg';

const BASE_URL = 'https://v2vlab-backend-24053985582.us-central1.run.app/api/v1';

const styles = {
  page: { fontFamily: "'Segoe UI', sans-serif", backgroundColor: '#fff', color: '#111827', minHeight: '100vh' },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 5%', height: '64px', borderBottom: '1px solid #e5e7eb',
    position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 50,
  },
  backBtn: {
    padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb',
    backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  layout: { display: 'grid', maxWidth: '1200px', margin: '0 auto', padding: '40px 24px', gap: '48px', alignItems: 'start' },
  toc: {
    position: 'sticky', top: '100px', fontSize: '13px',
    borderLeft: '2px solid #e5e7eb', paddingLeft: '16px',
  },
  tocTitle: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' },
  tocLink: {
    display: 'block', padding: '5px 0', color: '#6b7280', textDecoration: 'none',
    cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit',
    fontSize: '13px', textAlign: 'left', width: '100%',
  },
  h1: { fontSize: '32px', fontWeight: '800', marginBottom: '8px' },
  lede: { fontSize: '15px', color: '#6b7280', marginBottom: '32px', lineHeight: '1.6' },
  h2: {
    fontSize: '20px', fontWeight: '800', marginTop: '48px', marginBottom: '14px',
    paddingBottom: '8px', borderBottom: '1px solid #e5e7eb', scrollMarginTop: '90px',
  },
  h3: { fontSize: '15px', fontWeight: '700', marginTop: '28px', marginBottom: '10px' },
  p: { fontSize: '14px', color: '#374151', lineHeight: '1.75', marginBottom: '14px' },
  code: {
    backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px',
    fontFamily: 'monospace', fontSize: '13px', color: '#0052CC',
  },
  pre: {
    backgroundColor: '#111827', color: '#e5e7eb', borderRadius: '10px',
    padding: '18px 20px', fontSize: '12.5px', fontFamily: 'monospace',
    overflowX: 'auto', lineHeight: '1.7', marginBottom: '18px',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '18px', fontSize: '13px' },
  th: {
    backgroundColor: '#f9fafb', padding: '10px 12px', textAlign: 'left',
    fontWeight: '700', borderBottom: '1px solid #e5e7eb', color: '#374151',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', verticalAlign: 'top' },
  endpoint: {
    display: 'inline-block', fontFamily: 'monospace', fontSize: '13px',
    backgroundColor: '#EBF5FF', color: '#0052CC', padding: '6px 12px',
    borderRadius: '6px', fontWeight: '700', marginBottom: '10px',
  },
  note: {
    backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
    padding: '12px 16px', fontSize: '13px', color: '#92400e', lineHeight: '1.6', marginBottom: '18px',
  },
  info: {
    backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe', borderRadius: '8px',
    padding: '12px 16px', fontSize: '13px', color: '#1e40af', lineHeight: '1.6', marginBottom: '18px',
  },
};

const SECTIONS = [
  ['auth', 'Authentication'],
  ['limits', 'Rate Limits'],
  ['errors', 'Errors'],
  ['capabilities', 'Capabilities'],
  ['agents', 'Agents'],
  ['scenarios', 'Scenarios'],
  ['perturbation', 'Acoustic Perturbation'],
  ['simulations', 'Simulations'],
  ['evaluation', 'Evaluation Output'],
  ['corpus', 'Corpus Export'],
  ['example', 'Complete Example'],
  ['stability', 'API Stability'],
];

function Table({ head, rows }) {
  return (
    <table style={styles.table}>
      <thead>
        <tr>{head.map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} style={styles.td}>
                {typeof c === 'string' && c.startsWith('`') && c.endsWith('`')
                  ? <code style={styles.code}>{c.slice(1, -1)}</code>
                  : c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RestApiDocs() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const width = useWindowWidth();
  const isMobile = width < 1024;

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <img src={logo} alt="V2VLab" style={{ height: '28px', cursor: 'pointer' }}
          onClick={() => navigate(currentUser ? '/dashboard' : '/')} />
        <button style={styles.backBtn} onClick={() => navigate(currentUser ? '/developer' : '/')}>
          ← {currentUser ? 'Back to Developer' : 'Back to Home'}
        </button>
      </nav>

      <div style={{ ...styles.layout, gridTemplateColumns: isMobile ? '1fr' : '1fr 200px' }}>
        <div>
          <div style={styles.h1}>REST API Documentation</div>
          <div style={styles.lede}>
            Programmatic access to V2VLab for scripted and batch research workflows.
            Available on Professional and Enterprise plans.
          </div>
          <div style={styles.pre}>{BASE_URL}</div>

          {/* AUTH */}
          <div id="auth" style={styles.h2}>Authentication</div>
          <div style={styles.p}>Every request requires an API key in the <code style={styles.code}>Authorization</code> header:</div>
          <div style={styles.pre}>Authorization: Bearer v2v_live_xxxxxxxxxxxxxxxxxxxx</div>
          <div style={styles.p}>
            Generate a key from the <strong>Developer</strong> page. The full key is shown{' '}
            <strong>once</strong> — store it securely, it cannot be retrieved again. Keys are stored
            as SHA-256 hashes; V2VLab cannot recover your plaintext key.
          </div>
          <div style={styles.p}>
            Revoking a key takes effect immediately — any script using it stops working.
          </div>

          {/* LIMITS */}
          <div id="limits" style={styles.h2}>Rate Limits</div>
          <Table
            head={['Plan', 'Requests/min', 'Concurrent simulations']}
            rows={[['Professional', '60', '5'], ['Enterprise', '300', '25']]}
          />
          <div style={styles.p}>Exceeding the rate limit returns <code style={styles.code}>429</code> with retry headers:</div>
          <div style={styles.pre}>{`Retry-After: 37
X-RateLimit-Limit: 60
X-RateLimit-Remaining: 0`}</div>
          <div style={styles.p}>
            Turn-based usage limits also apply and are shared with the web UI — hitting them returns{' '}
            <code style={styles.code}>429</code> with code <code style={styles.code}>USAGE_LIMIT_REACHED</code>.
            Simulations idle for 10+ minutes stop counting toward the concurrency limit.
          </div>

          {/* ERRORS */}
          <div id="errors" style={styles.h2}>Errors</div>
          <Table
            head={['Status', 'Meaning']}
            rows={[
              ['`400`', 'Missing or invalid parameters'],
              ['`401`', 'Missing, invalid, or revoked API key'],
              ['`403`', 'Plan does not permit this action'],
              ['`404`', 'Not found, or not yet available (e.g. evaluation still processing)'],
              ['`429`', 'Rate, concurrency, or usage limit reached'],
              ['`500`', 'Server error'],
            ]}
          />

          {/* CAPABILITIES */}
          <div id="capabilities" style={styles.h2}>Capabilities</div>
          <div style={styles.endpoint}>GET /capabilities</div>
          <div style={styles.p}>
            Returns what your plan currently supports. Query this rather than hardcoding provider
            and model lists — it reflects live availability and your plan's entitlements.
            <code style={styles.code}>null</code> limits mean unlimited.
          </div>
          <div style={styles.pre}>{`curl "${BASE_URL}/capabilities" \\
  -H "Authorization: Bearer $KEY"`}</div>

          {/* AGENTS */}
          <div id="agents" style={styles.h2}>Agents</div>
          <div style={styles.p}>An agent is a conversational participant defined by a system prompt, an LLM, and optionally a voice.</div>
          <div style={styles.endpoint}>POST /agents</div>
          <Table
            head={['Field', 'Required', 'Notes']}
            rows={[
              ['`name`', 'Yes', 'Max 100 chars'],
              ['`system_prompt`', 'Yes', 'Max 2000 chars — defines persona and behavior'],
              ['`role`', 'No', 'Max 100 chars'],
              ['`llm_provider`', 'No', 'Default anthropic — must be permitted by your plan'],
              ['`llm_model`', 'No', 'Default claude-sonnet-4-5'],
              ['`voice_id`', 'No', 'ElevenLabs voice ID — required for voice output modes'],
            ]}
          />
          <div style={styles.pre}>{`curl -X POST "${BASE_URL}/agents" \\
  -H "Authorization: Bearer $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Patient A",
    "role": "patient",
    "system_prompt": "You are a patient with mild word-finding difficulty.",
    "voice_id": "EXAVITQu4vr4xnSDxMaL"
  }'`}</div>
          <div style={styles.p}>
            Also available: <code style={styles.code}>GET /agents</code> and{' '}
            <code style={styles.code}>GET /agents/:id</code>
          </div>

          {/* SCENARIOS */}
          <div id="scenarios" style={styles.h2}>Scenarios</div>
          <div style={styles.p}>A scenario binds agents together with shared context, output mode, and perturbation settings.</div>
          <div style={styles.endpoint}>POST /scenarios</div>
          <Table
            head={['Field', 'Required', 'Notes']}
            rows={[
              ['`title`', 'Yes', 'Max 150 chars'],
              ['`context`', 'Yes', 'Max 1000 chars — shared situation'],
              ['`agent_ids`', 'Yes', 'Minimum 2, subject to plan limit'],
              ['`max_turns`', 'No', 'Default 10, subject to plan limit'],
              ['`output_mode`', 'No', 'text (default), voice, or voice_stt'],
              ['`turn_order_mode`', 'No', 'round_robin (default) or hub_spoke'],
              ['`parametric_config`', 'No', 'Exact numeric perturbation control'],
              ['`perturbation_config`', 'No', 'Categorical preset perturbation'],
            ]}
          />

          {/* PERTURBATION */}
          <div id="perturbation" style={styles.h2}>Acoustic Perturbation</div>
          <div style={styles.p}>
            Perturbation is configured per-agent within a scenario. Only agents you specify are
            perturbed; others produce clean audio. Both config objects accept arbitrary parameter
            keys, so new parameters can be added without breaking existing integrations.
          </div>
          <div style={styles.note}>
            <strong>Scope note:</strong> These parameters produce controlled acoustic signal
            modifications. They are not clinically validated simulations of specific motor-speech
            disorders. Describe them as acoustic perturbations, not disorder reproductions, in
            published work.
          </div>
          <div style={styles.h3}>Parametric (exact numeric control)</div>
          <Table
            head={['Parameter', 'Unit', 'Range', 'Default']}
            rows={[
              ['`tremolo_freq`', 'Hz', '0–10', '0'],
              ['`tremolo_depth`', '0–1', '0–1', '0'],
              ['`vibrato_freq`', 'Hz', '0–10', '0'],
              ['`vibrato_depth`', '0–1', '0–1', '0'],
              ['`lowpass_freq`', 'Hz', '0–8000', '0 (off)'],
              ['`volume`', 'linear', '0.2–1.0', '1.0'],
              ['`speed`', 'ratio', '0.7–1.2', '1.0'],
              ['`pause_ms`', 'ms', '0–2000', '0'],
              ['`snr_db`', 'dB', '5–35', '35 (off)'],
              ['`tempo_min` / `tempo_max`', 'ratio', '0.7–1.35', '1.0'],
              ['`tilt_freq`', 'Hz', '1000–5000', '2000'],
              ['`tilt_gain`', 'dB', '-15–0', '0'],
              ['`aspiration_level`', '0–0.5', '0–0.5', '0'],
              ['`nasal_boost_gain`', 'dB', '0–12', '0'],
              ['`compressor_threshold`', 'dB', '-30–0', '0'],
              ['`channel_highpass_freq`', 'Hz', '0–500', '0'],
              ['`channel_lowpass_freq`', 'Hz', '2000–20000', '20000'],
            ]}
          />
          <div style={styles.pre}>{`"parametric_config": {
  "agents": {
    "AGENT_ID": {
      "tremolo_freq": 5,
      "tremolo_depth": 0.4,
      "volume": 0.7
    }
  }
}`}</div>
          <div style={styles.h3}>Categorical (preset levels)</div>
          <div style={styles.p}>
            Most accept <code style={styles.code}>none</code> / <code style={styles.code}>mild</code> /{' '}
            <code style={styles.code}>moderate</code> / <code style={styles.code}>severe</code>.
            Exceptions: <code style={styles.code}>pitch_variation</code>,{' '}
            <code style={styles.code}>background_noise</code>, <code style={styles.code}>channel_quality</code>.
          </div>
          <div style={styles.pre}>{`"perturbation_config": {
  "agents": {
    "AGENT_ID": {
      "vocal_tremor": "moderate",
      "hypophonia": "mild",
      "background_noise": "cafe"
    }
  }
}`}</div>
          <div style={styles.info}>
            Parametric takes precedence if both are supplied for the same agent.
          </div>

          {/* SIMULATIONS */}
          <div id="simulations" style={styles.h2}>Simulations</div>
          <div style={styles.endpoint}>POST /simulations</div>
          <div style={styles.p}>
            Returns <code style={styles.code}>201</code> immediately with{' '}
            <code style={styles.code}>status: "running"</code> — generation continues in the
            background. Poll <code style={styles.code}>GET /simulations/:id</code> for completion.
          </div>
          <div style={styles.info}>
            <strong>auto_advance</strong> (voice modes, default <code>true</code>): in the web UI,
            voice simulations advance when each turn's audio finishes playing. With no browser,
            nothing triggers that — <code>auto_advance</code> drives the conversation to completion
            server-side. Audio is still generated and stored for later retrieval.
          </div>
          <div style={styles.p}>
            <strong>Status values:</strong> <code style={styles.code}>running</code> ·{' '}
            <code style={styles.code}>completed</code> · <code style={styles.code}>stopped</code> ·{' '}
            <code style={styles.code}>failed</code> · <code style={styles.code}>limit_reached</code>
          </div>
          <div style={styles.endpoint}>GET /simulations/:id/transcript</div>
          <div style={styles.p}>
            <code style={styles.code}>content</code> is what the agent produced.{' '}
            <code style={styles.code}>perceived_content</code> is what STT transcribed{' '}
            <em>after</em> acoustic perturbation — present only on perturbed turns in{' '}
            <code style={styles.code}>voice_stt</code> mode. Comparing the two is the core research signal.
          </div>
          <div style={styles.note}>
            <code>audio_url</code> expires after 7 days. Use the corpus ZIP export for permanent archival.
          </div>

          {/* EVALUATION */}
          <div id="evaluation" style={styles.h2}>Evaluation Output</div>
          <div style={styles.endpoint}>GET /simulations/:id/evaluation</div>
          <div style={styles.p}>
            Returns <code style={styles.code}>404</code> while still processing — poll until available.
          </div>
          <div style={styles.h3}>Interpreting results</div>
          <Table
            head={['Field', 'How to read it']}
            rows={[
              ['`overall_score_basis`', 'scenario_weighted if the scenario defined metric weights; unweighted_mean_fallback otherwise. The fallback is a display convenience, NOT a validated composite — report individual metrics in research contexts.'],
              ['`scorable: false`', 'The judge abstained due to insufficient evidence rather than guessing. Treat as missing data, not a low score.'],
              ['`judge_agreement`', 'Professional/Enterprise runs the judge 3× and reports the median plus agreement. "low" agreement means that score is unstable — interpret cautiously.'],
              ['`computational_metrics`', 'Deterministic and reproducible (WER, CER, MATTR, MLU-w, TF-IDF coherence, lexical entrainment).'],
              ['`llm_metrics`', 'LLM-judged. Requires calibration against human expert ratings before research use.'],
            ]}
          />

          {/* CORPUS */}
          <div id="corpus" style={styles.h2}>Corpus Export</div>
          <div style={styles.endpoint}>GET /corpus/export</div>
          <Table
            head={['Param', 'Values']}
            rows={[
              ['`format`', 'json · csv · manifest · zip'],
              ['`simulation_ids`', 'comma-separated; omit for all'],
            ]}
          />
          <div style={styles.p}>
            <code style={styles.code}>zip</code> bundles actual audio files plus per-simulation
            transcripts — the only format that survives the 7-day URL expiry. Capped at 100
            simulations per request.
          </div>
          <div style={styles.pre}>{`curl "${BASE_URL}/corpus/export?format=zip" \\
  -H "Authorization: Bearer $KEY" \\
  -o corpus.zip`}</div>

          {/* EXAMPLE */}
          <div id="example" style={styles.h2}>Complete Example</div>
          <div style={styles.pre}>{`import requests, time

BASE = "${BASE_URL}"
HEADERS = {"Authorization": "Bearer v2v_live_YOUR_KEY"}

clinician = requests.post(f"{BASE}/agents", headers=HEADERS, json={
    "name": "Clinician",
    "system_prompt": "You are an SLP conducting an assessment. Keep responses brief.",
    "voice_id": "CwhRBWXzGAHq8TQ4Fs17",
}).json()

patient = requests.post(f"{BASE}/agents", headers=HEADERS, json={
    "name": "Patient",
    "system_prompt": "You are a patient with mild voice difficulty. Keep responses brief.",
    "voice_id": "EXAVITQu4vr4xnSDxMaL",
}).json()

scenario = requests.post(f"{BASE}/scenarios", headers=HEADERS, json={
    "title": "Voice Assessment",
    "context": "A brief clinical check-in about voice therapy progress.",
    "agent_ids": [clinician["id"], patient["id"]],
    "max_turns": 6,
    "output_mode": "voice_stt",
    "parametric_config": {
        "agents": {
            patient["id"]: {"tremolo_freq": 5, "tremolo_depth": 0.4, "volume": 0.7}
        }
    },
}).json()

sim = requests.post(f"{BASE}/simulations", headers=HEADERS,
                    json={"scenario_id": scenario["id"]}).json()

while True:
    time.sleep(3)
    status = requests.get(f"{BASE}/simulations/{sim['id']}", headers=HEADERS).json()
    print(f"{status['status']} — turn {status['current_turn']}/{status['max_turns']}")
    if status["status"] != "running":
        break

transcript = requests.get(f"{BASE}/simulations/{sim['id']}/transcript",
                          headers=HEADERS).json()
for t in transcript:
    print(f"[{t['turn_number']}] {t['agent_name']}: {t['content']}")
    if t.get("perceived_content"):
        print(f"    STT heard: {t['perceived_content']}")`}</div>

          {/* STABILITY */}
          <div id="stability" style={styles.h2}>API Stability</div>
          <div style={styles.p}>
            <code style={styles.code}>v1</code> follows additive-only versioning: new optional
            fields may be added, but existing fields are never removed or repurposed. Breaking
            changes would ship as <code style={styles.code}>v2</code> with a transition period.
          </div>
          <div style={styles.p}>
            Write clients tolerantly — ignore unrecognized response fields rather than failing on them.
          </div>

          <div style={{ ...styles.p, marginTop: '48px', paddingTop: '24px', borderTop: '1px solid #e5e7eb', color: '#6b7280' }}>
            Questions? <a href="mailto:info@converstation.ai" style={{ color: '#0052CC', fontWeight: '600' }}>info@converstation.ai</a>
          </div>
        </div>

        {/* TOC */}
        {!isMobile && (
          <div style={styles.toc}>
            <div style={styles.tocTitle}>On this page</div>
            {SECTIONS.map(([id, label]) => (
              <button
                key={id}
                style={styles.tocLink}
                onClick={() => scrollTo(id)}
                onMouseEnter={e => { e.currentTarget.style.color = '#0052CC'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#6b7280'; }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
